import { createFileRoute, Link } from "@tanstack/react-router";
import { VictoryCardsHub } from "@/components/viral/VictoryCardsHub";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trophy, Sparkles, Share2, Flame, Crown, TrendingUp, Download } from "lucide-react";
import { LanguageDropdown } from "@/components/i18n/LanguageDropdown";
import { useLanguage } from "@/hooks/useLanguage";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { PREVIEW_MODE } from "@/lib/previewMode";
import { OFFICIAL_MATCH } from "@/lib/officialMatch";
import {
  buildPersonalClip,
  downloadDataUrl,
  exportAndShareToWhatsApp,
  type PersonalClipPayload,
} from "@/lib/clipExport/personalClips";
import { buildCanonicalShareUrl } from "@/lib/share/canonicalShare";

// Map a demo card to the premium personal-clip payload so every share
// button in this route ends in the same PNG-first native share (or wa.me
// fallback) — no more "text-only" branch.
function demoToPersonalClip(d: Demo): PersonalClipPayload {
  return {
    kind: "mi_clip_viral",
    nation: d.nation,
    headline: d.headline,
    subline: d.subline,
    handle: "VerseSport",
  };
}

async function sharePremiumCard(d: Demo) {
  const shareUrl = buildCanonicalShareUrl("/viral-cards", {
    source: "viral_cards",
    medium: "share",
    campaign: "wa_victory",
  });
  await exportAndShareToWhatsApp(demoToPersonalClip(d), shareUrl);
}

async function downloadPremiumCard(d: Demo) {
  const dataUrl = await buildPersonalClip(demoToPersonalClip(d));
  downloadDataUrl(dataUrl, `versesport-${d.nation.toLowerCase().replace(/\s+/g, "-")}.png`);
}

export const Route = createFileRoute("/viral-cards")({
  head: () => ({
    meta: [
      { title: "Viral Victory Cards — VerseSport" },
      { name: "description", content: "Convierte tus logros futbolísticos en tarjetas listas para WhatsApp, Instagram, TikTok, Facebook y X." },
    ],
  }),
  component: ViralCardsPage,
});

type Demo = {
  flag: string;
  nation: string;
  headline: string;
  subline: string;
  emoji: string;
  gradient: string;
};

const DEMO_CARDS: Demo[] = [
  { flag: "🇧🇷", nation: "Brasil", headline: "Oracle Mundial", subline: "95% de aciertos en fase de grupos", emoji: "🔮", gradient: "from-yellow-500/30 via-green-600/30 to-blue-600/30" },
  { flag: "🇦🇷", nation: "Argentina", headline: "Predicción Perfecta", subline: "Marcador exacto 3-1 acertado", emoji: "🎯", gradient: "from-sky-400/30 via-white/20 to-sky-500/30" },
  { flag: "🇲🇽", nation: "México", headline: "Campeón Nation Wars", subline: "#1 en el ranking global semanal", emoji: "👑", gradient: "from-green-700/30 via-white/20 to-red-600/30" },
  { flag: "🇨🇴", nation: "Colombia", headline: "Racha Imparable", subline: "12 predicciones acertadas seguidas", emoji: "🔥", gradient: "from-yellow-400/30 via-blue-600/30 to-red-500/30" },
];

const NATION_GLORY: Demo[] = [
  { flag: "🇧🇷", nation: "Brasil", headline: "#1 del día", subline: "Lidera el ranking mundial hoy", emoji: "🏆", gradient: "from-yellow-500/30 to-green-600/30" },
  { flag: "🇦🇷", nation: "Argentina", headline: "Venció a Brasil", subline: "Histórico choque sudamericano", emoji: "⚔️", gradient: "from-sky-400/30 to-sky-600/30" },
  { flag: "🇲🇽", nation: "México", headline: "Lidera Nation Wars", subline: "Comunidad imparable esta semana", emoji: "👑", gradient: "from-green-700/30 to-red-600/30" },
  { flag: "🇨🇴", nation: "Colombia", headline: "Escaló posiciones", subline: "+5 puestos en 24 horas", emoji: "📈", gradient: "from-yellow-400/30 to-red-500/30" },
];

// PREVIEW MODE — Card decks pinned to OFFICIAL_MATCH (España vs
// Argentina). Six premium card types: Goal, Victory ESP, Victory ARG,
// Prediction, Momentum, Fan Pride + Debate.
const PREVIEW_DEMO_CARDS: Demo[] = [
  { flag: "⚽", nation: OFFICIAL_MATCH.competition, headline: "¡GOOOOL!", subline: `${OFFICIAL_MATCH.homeName} 2 – 1 ${OFFICIAL_MATCH.awayName}`, emoji: "🥅", gradient: "from-amber-500/40 via-red-600/30 to-black/40" },
  { flag: OFFICIAL_MATCH.homeFlag, nation: OFFICIAL_MATCH.homeName, headline: "¡Somos Campeones!", subline: "España conquista el Mundial", emoji: "🏆", gradient: "from-red-600/40 via-yellow-500/30 to-red-700/40" },
  { flag: OFFICIAL_MATCH.awayFlag, nation: OFFICIAL_MATCH.awayName, headline: "¡Vamos Argentina!", subline: "La Albiceleste no se rinde", emoji: "💙", gradient: "from-sky-400/40 via-white/25 to-sky-600/40" },
  { flag: "🎯", nation: "Predicción", headline: "Yo acerté el marcador", subline: `${OFFICIAL_MATCH.homeName} 2 – 1 ${OFFICIAL_MATCH.awayName}`, emoji: "🔮", gradient: "from-emerald-500/35 via-primary/25 to-emerald-700/35" },
];

const PREVIEW_NATION_GLORY: Demo[] = [
  { flag: OFFICIAL_MATCH.homeFlag, nation: OFFICIAL_MATCH.homeName, headline: "La Roja domina", subline: "Posesión, presión y precisión", emoji: "🔴", gradient: "from-red-600/40 to-yellow-500/40" },
  { flag: OFFICIAL_MATCH.awayFlag, nation: OFFICIAL_MATCH.awayName, headline: "Argentina empuja", subline: "Presión total por el empate", emoji: "⚡", gradient: "from-sky-400/40 to-sky-700/40" },
  { flag: "👑", nation: "MVP del partido", headline: "Jugador del momento", subline: "El fan lo eligió en vivo", emoji: "🌟", gradient: "from-amber-400/40 to-orange-600/40" },
  { flag: "⚖️", nation: "Debate", headline: "¿Fue penalti?", subline: "Sí · No — vota con tu nación", emoji: "🗣️", gradient: "from-violet-500/40 to-fuchsia-600/40" },
];

const ACTIVE_DEMO_CARDS = PREVIEW_MODE ? PREVIEW_DEMO_CARDS : DEMO_CARDS;
const ACTIVE_NATION_GLORY = PREVIEW_MODE ? PREVIEW_NATION_GLORY : NATION_GLORY;

/**
 * Catálogo extendido de equipos/selecciones para que el usuario pueda elegir
 * su equipo de preferencia. Si su equipo no está en las tarjetas destacadas,
 * lo añadimos al tope de la galería como "Tu equipo".
 */
const PREFERRED_TEAMS: { code: string; flag: string; nation: string; gradient: string }[] = [
  { code: "BRA", flag: "🇧🇷", nation: "Brasil", gradient: "from-yellow-500/30 to-green-600/30" },
  { code: "ARG", flag: "🇦🇷", nation: "Argentina", gradient: "from-sky-400/30 to-sky-600/30" },
  { code: "MEX", flag: "🇲🇽", nation: "México", gradient: "from-green-700/30 to-red-600/30" },
  { code: "COL", flag: "🇨🇴", nation: "Colombia", gradient: "from-yellow-400/30 to-red-500/30" },
  { code: "USA", flag: "🇺🇸", nation: "Estados Unidos", gradient: "from-blue-700/30 to-red-600/30" },
  { code: "ESP", flag: "🇪🇸", nation: "España", gradient: "from-red-600/30 to-yellow-500/30" },
  { code: "FRA", flag: "🇫🇷", nation: "Francia", gradient: "from-blue-700/30 to-red-500/30" },
  { code: "GER", flag: "🇩🇪", nation: "Alemania", gradient: "from-zinc-700/30 to-yellow-500/30" },
  { code: "ITA", flag: "🇮🇹", nation: "Italia", gradient: "from-green-600/30 to-blue-600/30" },
  { code: "POR", flag: "🇵🇹", nation: "Portugal", gradient: "from-green-700/30 to-red-600/30" },
  { code: "ENG", flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", nation: "Inglaterra", gradient: "from-zinc-200/30 to-red-600/30" },
  { code: "NED", flag: "🇳🇱", nation: "Países Bajos", gradient: "from-orange-500/30 to-blue-700/30" },
  { code: "BEL", flag: "🇧🇪", nation: "Bélgica", gradient: "from-zinc-800/30 to-red-600/30" },
  { code: "URU", flag: "🇺🇾", nation: "Uruguay", gradient: "from-sky-400/30 to-yellow-500/30" },
  { code: "CHI", flag: "🇨🇱", nation: "Chile", gradient: "from-red-600/30 to-blue-700/30" },
  { code: "PER", flag: "🇵🇪", nation: "Perú", gradient: "from-red-600/30 to-zinc-200/30" },
  { code: "ECU", flag: "🇪🇨", nation: "Ecuador", gradient: "from-yellow-500/30 to-red-600/30" },
  { code: "PAR", flag: "🇵🇾", nation: "Paraguay", gradient: "from-red-600/30 to-blue-700/30" },
  { code: "VEN", flag: "🇻🇪", nation: "Venezuela", gradient: "from-yellow-500/30 to-red-600/30" },
  { code: "CRC", flag: "🇨🇷", nation: "Costa Rica", gradient: "from-red-600/30 to-blue-700/30" },
  { code: "JPN", flag: "🇯🇵", nation: "Japón", gradient: "from-zinc-200/30 to-red-600/30" },
  { code: "KOR", flag: "🇰🇷", nation: "Corea del Sur", gradient: "from-zinc-200/30 to-blue-700/30" },
  { code: "MAR", flag: "🇲🇦", nation: "Marruecos", gradient: "from-red-700/30 to-green-700/30" },
  { code: "OTRO", flag: "🌍", nation: "Otro / Club", gradient: "from-zinc-700/30 to-zinc-500/30" },
];

const PREF_KEY = "vs:preferred_team";

function DemoCard({ d }: { d: Demo; sampleLabel?: string; shareable?: boolean; onShare?: (d: Demo) => void }) {
  const [busy, setBusy] = useState<null | "share" | "download">(null);
  const doShare = async () => {
    if (busy) return;
    setBusy("share");
    try {
      await sharePremiumCard(d);
    } catch { toast.error("No se pudo compartir"); }
    finally { setBusy(null); }
  };
  const doDownload = async () => {
    if (busy) return;
    setBusy("download");
    try {
      await downloadPremiumCard(d);
      toast.success("Tarjeta descargada");
    } catch { toast.error("No se pudo descargar"); }
    finally { setBusy(null); }
  };
  return (
    <div
      className={`group relative aspect-[9/16] overflow-hidden rounded-[22px] border border-white/10 bg-black shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)] ring-1 ring-white/5`}
    >
      {/* Base color wash — deeper saturation for social-ready contrast. */}
      <div className={`absolute inset-0 bg-gradient-to-br ${d.gradient}`} />
      <div className={`absolute inset-0 bg-gradient-to-tr ${d.gradient} opacity-70 mix-blend-screen`} />
      {/* Radial spotlight — draws the eye to the emoji/headline. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 90% at 50% 35%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.05) 40%, rgba(0,0,0,0.0) 65%)",
        }}
      />
      {/* Holo sweep — diagonal shine that shifts on hover. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-x-10 top-0 h-full opacity-60 mix-blend-overlay transition-transform duration-700 ease-out group-hover:translate-x-6"
        style={{
          background:
            "linear-gradient(115deg, transparent 20%, rgba(255,255,255,0.45) 45%, rgba(255,255,255,0.05) 55%, transparent 80%)",
        }}
      />
      {/* Micro noise grain — kills flat AI look, gives it a printed feel. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.7 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          backgroundSize: "180px 180px",
        }}
      />
      {/* Bottom scrim — deep dark to make text pop on any color. */}
      <div className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-black via-black/70 to-transparent" />
      {/* Inner border + subtle vignette. */}
      <div className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-inset ring-white/15" />
      <div className="pointer-events-none absolute inset-0 rounded-[22px] shadow-[inset_0_-80px_120px_-40px_rgba(0,0,0,0.65)]" />

      <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-1.5">
        <button
          type="button"
          onClick={doDownload}
          disabled={busy !== null}
          aria-label="Descargar"
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur ring-1 ring-white/20 transition active:scale-95 disabled:opacity-60"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={doShare}
          disabled={busy !== null}
          className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-black shadow-md backdrop-blur transition active:scale-95 disabled:opacity-60"
        >
          <Share2 className="h-3 w-3" />
          {busy === "share" ? "…" : "Compartir"}
        </button>
      </div>
      <div className="absolute top-2.5 left-2.5 rounded-xl bg-black/55 px-2.5 py-1.5 text-[26px] leading-none backdrop-blur-md ring-1 ring-white/15">
        {d.flag}
      </div>

      <div className="absolute inset-x-0 bottom-0 p-4 space-y-1.5 text-white">
        <div className="text-[38px] leading-none drop-shadow-[0_3px_10px_rgba(0,0,0,0.5)]">{d.emoji}</div>
        <div className="text-[9.5px] font-black uppercase tracking-[0.28em] text-white/70">
          {d.nation}
        </div>
        <div
          className="text-[19px] font-black leading-[1.05] tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]"
          style={{ fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif' }}
        >
          {d.headline}
        </div>
        <div className="text-[11.5px] leading-snug text-white/85">{d.subline}</div>
        <div className="mt-2 flex items-center justify-between border-t border-white/10 pt-2 text-[9px] font-black uppercase tracking-[0.22em]">
          <span className="flex items-center gap-1 text-white/80">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[oklch(0.7_0.22_25)]" />
            VerseSport™
          </span>
          <span className="rounded-full bg-white/15 px-2 py-0.5 text-[8.5px] backdrop-blur ring-1 ring-white/15">
            Final Mundial
          </span>
        </div>
      </div>
    </div>
  );
}

const STEPS = [
  { n: 1, t: "Participa en una predicción", icon: Sparkles },
  { n: 2, t: "Gana XP", icon: TrendingUp },
  { n: 3, t: "Desbloquea un logro", icon: Trophy },
  { n: 4, t: "Genera tu tarjeta", icon: Crown },
  { n: 5, t: "Compártela", icon: Share2 },
];

function ViralCardsPage() {
  const { lang } = useLanguage();
  const sampleLabel = lang === "EN" ? "Sample" : lang === "PT" ? "Exemplo" : "Ejemplo";
  const shareLabel = lang === "EN" ? "Share" : lang === "PT" ? "Compartilhar" : "Compartir";
  const [preferred, setPreferred] = useState<string>("");
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(PREF_KEY);
      if (saved) setPreferred(saved);
    } catch { /* ignore */ }
  }, []);
  const onPickTeam = (code: string) => {
    setPreferred(code);
    try { window.localStorage.setItem(PREF_KEY, code); } catch { /* ignore */ }
    const team = PREFERRED_TEAMS.find((t) => t.code === code);
    if (team) {
      toast.success(
        lang === "EN"
          ? `Preferred team set: ${team.nation}`
          : lang === "PT"
          ? `Time preferido: ${team.nation}`
          : `Equipo de preferencia: ${team.nation}`
      );
    }
  };
  const preferredTeam = PREFERRED_TEAMS.find((t) => t.code === preferred);
  const prefLabel = lang === "EN" ? "Your favorite team" : lang === "PT" ? "Seu time favorito" : "Tu equipo de preferencia";
  const prefHint = lang === "EN"
    ? "We'll prioritize cards for this team."
    : lang === "PT"
    ? "Vamos priorizar cards deste time."
    : "Priorizaremos tarjetas de este equipo.";
  // Unified premium share for the page-level CTA — same engine as every
  // DemoCard, so pressing "Compartir" always renders a PNG-first share.
  const handleShare = async () => {
    const hero = ACTIVE_DEMO_CARDS[0] ?? DEMO_CARDS[0];
    await sharePremiumCard(hero);
  };
  return (
    <main className="container mx-auto p-4 md:p-6 space-y-8">
      {/* Mobile-reachable controls: language + share, restored from header
          (this route has no global header). */}
      <div className="flex items-center justify-end gap-2">
        <LanguageDropdown />
        <Button size="sm" onClick={handleShare} className="gap-1.5">
          <Share2 className="h-4 w-4" /> {shareLabel}
        </Button>
      </div>
      <section className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/20 via-background to-accent/20 p-6 md:p-10">
        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />
        <div className="relative space-y-3 max-w-3xl">
          <Badge className="bg-primary/20 text-primary border-primary/40">
            <Flame className="h-3 w-3 mr-1" /> NUEVO · MUNDIAL 2026
          </Badge>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight">
            Viral Victory Cards<span className="text-primary">™</span>
          </h1>
          <p className="text-sm md:text-lg text-muted-foreground">
            Convierte tus logros futbolísticos en tarjetas listas para
            WhatsApp, Instagram, TikTok, Facebook y X.
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button asChild>
              <Link to="/">Ir al Match Viewer</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/nation-wars">Nation Wars</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-end justify-between gap-2">
          <div>
            <h2 className="text-xl md:text-2xl font-black">Galería de demostración</h2>
            <p className="text-xs text-muted-foreground">Así se verán tus tarjetas cuando desbloquees tus logros.</p>
          </div>
          <Badge variant="outline" className="text-[10px] shrink-0">{sampleLabel}</Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {ACTIVE_DEMO_CARDS.map((d) => <DemoCard key={d.headline} d={d} sampleLabel={sampleLabel} />)}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl md:text-2xl font-black">Cómo conseguir tu primera tarjeta</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.n} className="bg-background/60">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-black">{s.n}</div>
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-xs font-semibold leading-snug">{s.t}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-end justify-between gap-2">
          <div>
            <h2 className="text-xl md:text-2xl font-black">Nation Glory Cards<span className="text-primary">™</span></h2>
            <p className="text-xs text-muted-foreground">Tarjetas que celebran momentos históricos de cada nación.</p>
          </div>
        </div>

        {/* Preferred-team picker — addresses "¿dónde elijo mi equipo?" */}
        <div className="rounded-2xl border border-border/60 bg-card/60 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <label htmlFor="preferred-team" className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
              {prefLabel}
            </label>
            {preferredTeam && (
              <span className="text-[11px] font-bold inline-flex items-center gap-1">
                <span className="text-base leading-none">{preferredTeam.flag}</span>
                {preferredTeam.nation}
              </span>
            )}
          </div>
          <select
            id="preferred-team"
            value={preferred}
            onChange={(e) => onPickTeam(e.target.value)}
            className="w-full min-h-[44px] rounded-xl border border-border/60 bg-background px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="">{lang === "EN" ? "Select your team…" : lang === "PT" ? "Selecione seu time…" : "Selecciona tu equipo…"}</option>
            {PREFERRED_TEAMS.map((tm) => (
              <option key={tm.code} value={tm.code}>{tm.flag}  {tm.nation}</option>
            ))}
          </select>
          <p className="text-[10px] text-muted-foreground">{prefHint}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {preferredTeam && !ACTIVE_NATION_GLORY.some((n) => n.nation === preferredTeam.nation) && (
            <DemoCard
              d={{
                flag: preferredTeam.flag,
                nation: preferredTeam.nation,
                headline: lang === "EN" ? "Your team" : lang === "PT" ? "Seu time" : "Tu equipo",
                subline: lang === "EN" ? "Cards personalized for you" : lang === "PT" ? "Cards personalizados pra você" : "Tarjetas personalizadas para ti",
                emoji: "⭐",
                gradient: preferredTeam.gradient,
              }}
            />
          )}
          {ACTIVE_NATION_GLORY.map((d) => (
            <DemoCard key={d.headline} d={d} />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl md:text-2xl font-black">Tus tarjetas</h2>
        <p className="text-xs text-muted-foreground">Generadas en 1080×1920 con tus logros reales — listas para WhatsApp, Instagram y TikTok.</p>
        <VictoryCardsHub />
      </section>
    </main>
  );
}